<template>
  <div :class="$style.timetable">
    <div
      v-for="(c, idx) in columns"
      :key="title(c.date)"
      :class="[$style.timetable__column, $style.column]"
    >
      <div :class="[$style.column__header, $style.header]">
        <h2 :class="titleStyle(c.date)">{{ title(c.date) }}</h2>
        <ButtonIconOnly
          v-if="idx === 0"
          :class="$style.header__prev"
          @click="$emit('onPrevClicked')"
          :disabled="!hasPrev"
        >
          <template #icon>
            <IconChevron direction="left" />
          </template>
        </ButtonIconOnly>
        <ButtonIconOnly
          :class="$style.header__next"
          @click="$emit('onNextClicked')"
          v-if="idx === columns.length - 1"
        >
          <template #icon>
            <IconChevron direction="right" />
          </template>
        </ButtonIconOnly>
      </div>
      <ol :class="[$style.column__body, $style.body]">
        <li
          v-for="ts in c.availableTimeSlots"
          :key="`${ts.startsAt}::${ts.endsAt}`"
        >
          <TimeSlotCard
            :timeSlot="ts"
            variant="concise"
            :isActive="isPicked(ts)"
            @click="$emit('update:modelValue', togglePicked(ts))"
          />
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup lang="ts">
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import TimeSlotCard from '@/components/organisms/TimeSlotPickerModal/TimeSlotCard.vue'
import IconChevron from '@/components/icons/IconChevron.vue'

import { ja } from 'date-fns/locale'
import { toRefs, useCssModule, ref, onMounted } from 'vue'
import { type PickedTimeSlots, type TimeSlot, useTimeSlot } from './useTimeSlot'
import { identity, uniq } from 'lodash-es'
import * as kenall from '@/hooks/useKenAll'

import { format } from 'date-fns'

export type Column = {
  date: Date
  availableTimeSlots: TimeSlot[]
}
const props = defineProps<{
  modelValue: PickedTimeSlots
  columns: Column[]
  hasPrev: boolean
}>()
const { modelValue } = toRefs(props)

defineEmits<{
  (e: 'update:modelValue', value: TimeSlot[]): void
  (e: 'onPrevClicked'): void
  (e: 'onNextClicked'): void
}>()

const { isPicked, togglePicked } = useTimeSlot(modelValue)

const title = (d: Date) => format(d, 'Y/M/d (E)', { locale: ja })
const style = useCssModule()

const holidays = ref<string[]>([])
const isHoliday = (d: Date) => holidays.value.includes(format(d, 'yyyy-MM-dd'))

const titleStyle = (d: Date) => [
  style.header__title,
  {
    [style['header__title--holiday']]: isHoliday(d),
    [style['header__title--sunday']]: !isHoliday(d) && d.getDay() === 0,
    [style['header__title--saturday']]: !isHoliday(d) && d.getDay() === 6,
  },
]

onMounted(async () => {
  const years = uniq(props.columns.map(c => c.date.getFullYear()))
  holidays.value = (
    await Promise.all(
      years.map(async y => {
        const holidays = await kenall.getHolidaysYear(y)
        return holidays.data.map(d => d.date)
      })
    )
  ).flatMap(identity<string[]>)
})
</script>

<style module lang="scss">
.timetable {
  display: flex;

  &__column {
    flex: 1;
  }
}

.column {
  display: flex;
  flex-direction: column;
  height: 100%;

  &:nth-child(n + 1) {
    border-left: 1px solid $color-border-low-emphasis;
  }

  &__header {
    padding: 8px;
    border-bottom: 1px solid $color-border-low-emphasis;
  }

  &__body {
    padding: 8px;
    flex: 1;
  }
}

.header {
  position: relative;

  &__title {
    width: 100%;
    padding: 4px;
    font-size: 12px;
    font-weight: 700;
    text-align: center;

    &--sunday,
    &--holiday {
      color: $color-label-red;
    }

    &--saturday {
      color: $color-label-blue;
    }
  }

  &__prev {
    font-size: 24px;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  &__next {
    font-size: 24px;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}

.body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
